import mixpanel from 'mixpanel-browser';
import isEmpty from 'lodash/isEmpty';
import toFinite from 'lodash/toFinite';
import BaseProvider from './BaseProvider';
import { getUserId } from '../../../utilities/userTokenDetails';

const globalKey = 'mixpanel';

// shared mixpanel instance that ensures it's initialised only once
// and is accessible by different scripts
const sharedInstance = () => window[globalKey];
const setSharedInstance = (instance) => window[globalKey] = instance;

class MixpanelProvider extends BaseProvider {
    isInitialised() {
        return sharedInstance() != null;
    }

    init() {
        mixpanel.init(process.env.MIXPANEL_TOKEN);
        setSharedInstance(mixpanel);
    }

    trackEvent({ name, details = {}, trackingOptions = {} }) {
        const userId = getTrackingUserId(details?.providerProps?.userProfileProps?.valueProps);

        // do not track events if the user is not logged in.
        // Should only track logged in events to prevent
        // mixpanel having undefined values
        if (!isValidUserId(userId)) {
            return;
        }

        if (!isValidEventName(name)) {
            return;
        }

        const { baseProps = {}, providerProps = {} } = details;
        const { userProfileProps = {}, superProps = {} } = providerProps;

        // set user profile properties first, followed by "super" / common
        // properties so that tracked event has the applicable properties applied
        setUserProfile(userProfileProps);
        setCommonEventProperties(superProps);
        sharedInstance().track(name, baseProps, trackingOptions);
    }

    resetIdentity() {
        sharedInstance().reset();
    }
}

// used to identify user via common auth platform across sessions and devices
export const userIdProp = 'userId';

const getTrackingUserId = (valueProps) => {
    // Get the userId from the decoded token if it's in the DOM.
    // During login event via the modal we do not have the auth token in the DOM
    // as it's added after the page reload. Therefore, we can take it from the props
    // that is set during the login mixpanel event.
    return getUserId() ?? valueProps?.immutable[userIdProp] ?? null;
};

const setUserProfile = ({ valueProps = {}, incrementalProps = {} }) => {
    // immutable properties should only be set once e.g. userId, signUpDate
    const { immutable = {}, mutable = {} } = valueProps;
    const userId = getTrackingUserId(valueProps);

    // only logged in users with a defined user id should have user profile properties set
    if (!isValidUserId(userId)) {
        return;
    }

    sharedInstance().identify(userId);
    setImmutableUserProps(immutable);
    setMutableUserProps(mutable);
    setIncrementalUserProps(incrementalProps);
};

const isValidUserId = (userId) => typeof userId === 'string' && userId !== '';
const isValidEventName = (name) => typeof name === 'string' && name !== '';

const userPropMethods = {
    mutable: 'set',
    immutable: 'set_once',
    increment: 'increment',
};

const setImmutableUserProps = (props) => setValidatedUserProps(props, userPropMethods.immutable);

const setMutableUserProps = (props) => setValidatedUserProps(props, userPropMethods.mutable);

const setIncrementalUserProps = (props) => setValidatedUserProps(props, userPropMethods.increment);

const setValidatedUserProps = (props, method) => {
    setValidatedEventProps(props, () => sharedInstance().people[method](props));
};

/**
 * Mixpanel refers to common event properties as "super" properties
 * This allows a collection of properties to be preset for all events
 */
const setCommonEventProperties = ({ valueProps = {}, incrementalProps = {} }) => {
    // immutable properties should only be set once e.g. userId, signUpDate
    const { immutable = {}, mutable = {} } = valueProps;

    setImmutableCommonEventProps(immutable);
    setMutableCommonEventProps(mutable);
    setIncrementalCommonEventProps(incrementalProps);
};

const commonEventPropMethods = {
    mutable: 'register',
    immutable: 'register_once',
};

const setImmutableCommonEventProps = (props) => {
    setValidatedCommonEventProps(props, commonEventPropMethods.immutable);
};

const setMutableCommonEventProps = (props) => {
    setValidatedCommonEventProps(props, commonEventPropMethods.mutable);
};

const setIncrementalCommonEventProps = (props) => {
    const incrementedProps = {};
    Object.entries(props).forEach(([attributeName, value]) => {
        // Will return zero for non-number strings and null.
        const existingValue = toFinite(sharedInstance().get_property(attributeName));

        // Allow floats and negative values.
        incrementedProps[attributeName] = existingValue + value;
    });

    setMutableCommonEventProps(incrementedProps);
};

const setValidatedCommonEventProps = (props, method) => {
    setValidatedEventProps(props, () => sharedInstance()[method](props));
};

const setValidatedEventProps = (props, methodCallback) => {
    if (isEmpty(props)) {
        return;
    }

    methodCallback();
};

export default MixpanelProvider;
